import React, { useState } from "react";
import { Activity, AlertCircle, Check, RefreshCw, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { firebaseAuth } from "../../services/firebase-auth";

const FCMDebugPanel = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
    const [result, setResult] = useState<any>(null);
    const [userId, setUserId] = useState("");
    const [showDetails, setShowDetails] = useState(false);

    const testConnection = async () => {
        try {
            setIsLoading(true);
            setStatus("idle");
            setResult(null);

            // Obtener token de autenticación
            const idToken = await firebaseAuth.getIdToken();

            const response = await fetch(
                `${import.meta.env.VITE_API_URL}/api/notifications/test-fcm-connectivity`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                }
            );

            const data = await response.json();

            if (response.ok && data.success) {
                setStatus("success");
            } else {
                setStatus("error");
            }

            setResult(data);
        } catch (error) {
            console.error("Error testing FCM connectivity:", error);
            setStatus("error");
            setResult({
                success: false,
                message: error instanceof Error ? error.message : "Unknown error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getUserTokens = async () => {
        if (!userId.trim()) {
            setResult({
                success: false,
                message: "Por favor, ingresa un ID de usuario",
            });
            setStatus("error");
            return;
        }

        try {
            setIsLoading(true);
            setStatus("idle");
            setResult(null);

            // Obtener token de autenticación
            const idToken = await firebaseAuth.getIdToken();

            const response = await fetch(
                `${import.meta.env.VITE_API_URL}/api/notifications/user-tokens/${userId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                }
            );

            const data = await response.json();

            if (response.ok && data.success) {
                setStatus("success");
            } else {
                setStatus("error");
            }

            setResult(data);
        } catch (error) {
            console.error("Error getting user tokens:", error);
            setStatus("error");
            setResult({
                success: false,
                message: error instanceof Error ? error.message : "Unknown error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const resetTokens = async () => {
        if (!userId.trim() || !confirm(`¿Estás seguro de eliminar TODOS los tokens del usuario ${userId}?`)) {
            return;
        }

        try {
            setIsLoading(true);
            setStatus("idle");
            setResult(null);

            // Obtener token de autenticación
            const idToken = await firebaseAuth.getIdToken();

            const response = await fetch(
                `${import.meta.env.VITE_API_URL}/api/notifications/reset-tokens/${userId}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                }
            );

            const data = await response.json();

            if (response.ok && data.success) {
                setStatus("success");
            } else {
                setStatus("error");
            }

            setResult(data);
        } catch (error) {
            console.error("Error resetting tokens:", error);
            setStatus("error");
            setResult({
                success: false,
                message: error instanceof Error ? error.message : "Unknown error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-6">
            <div className="flex items-center mb-4">
                <Activity className="mr-2 text-gray-500 dark:text-gray-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Diagnóstico FCM
                </h2>
            </div>

            <div className="space-y-4">
                {/* Test Connectivity */}
                <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Verificar conectividad con Firebase Cloud Messaging
                    </h3>
                    <button
                        onClick={testConnection}
                        disabled={isLoading}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                    >
                        {isLoading ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Check className="w-4 h-4 mr-2" />
                        )}
                        Probar conexión
                    </button>
                </div>

                {/* User Tokens */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Verificar tokens de usuario
                    </h3>
                    <div className="flex space-x-2">
                        <input
                            type="text"
                            placeholder="ID de usuario"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md flex-grow dark:bg-gray-700 dark:text-white"
                        />
                        <button
                            onClick={getUserTokens}
                            disabled={isLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                        >
                            {isLoading ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Check className="w-4 h-4 mr-2" />
                            )}
                            Verificar
                        </button>
                        <button
                            onClick={resetTokens}
                            disabled={isLoading || !userId}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                            title="Eliminar todos los tokens del usuario"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Results */}
                {result && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="mt-4"
                    >
                        <div
                            className={`p-4 rounded-md ${status === "success"
                                    ? "bg-green-50 dark:bg-green-900/30"
                                    : status === "error"
                                        ? "bg-red-50 dark:bg-red-900/30"
                                        : "bg-gray-50 dark:bg-gray-700"
                                }`}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex">
                                    {status === "success" ? (
                                        <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                                    ) : status === "error" ? (
                                        <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                                    ) : null}
                                    <div>
                                        <p
                                            className={`font-medium ${status === "success"
                                                    ? "text-green-800 dark:text-green-400"
                                                    : status === "error"
                                                        ? "text-red-800 dark:text-red-400"
                                                        : "text-gray-800 dark:text-gray-200"
                                                }`}
                                        >
                                            {result.message || (result.success ? "Operación exitosa" : "Error")}
                                        </p>
                                        {result.tokens && (
                                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                                Tokens encontrados: {result.count || result.tokens.length}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowDetails(!showDetails)}
                                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    {showDetails ? "Ocultar detalles" : "Ver detalles"}
                                </button>
                            </div>

                            {showDetails && (
                                <pre className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-auto max-h-80">
                                    {JSON.stringify(result, null, 2)}
                                </pre>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default FCMDebugPanel;